import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, X } from 'lucide-react';
import { PriceBreakdown } from './PriceBreakdown';

const API = process.env.REACT_APP_BACKEND_URL;
const blank = { name: '', email: '', phone: '', college: '' };
const EMAIL_RX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const inp = 'w-full px-3 py-2.5 border border-[#E4E4E7] rounded text-sm text-[#0A0A0A] bg-white focus:outline-none focus:border-[#002FA7]';

const startCashfree = async (sessionId, returnUrl) => {
  const { load } = await import('@cashfreepayments/cashfree-js');
  const cashfree = await load({ mode: 'production' });
  cashfree.checkout({ paymentSessionId: sessionId, returnUrl });
};

const MemberRow = ({ i, m, onChange, onRemove, canRemove }) => (
  <div className="flex items-start gap-2" data-testid={`guest-member-row-${i}`}>
    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
      <input className={inp} placeholder={`Member ${i + 2} full name`} value={m.name} onChange={(e) => onChange({ ...m, name: e.target.value })} data-testid={`guest-member-name-${i}`} />
      <input className={inp} type="email" placeholder="Member email" value={m.email} onChange={(e) => onChange({ ...m, email: e.target.value })} data-testid={`guest-member-email-${i}`} />
    </div>
    <button type="button" onClick={onRemove} disabled={!canRemove} className="px-3 py-2 text-[#A1A1AA] hover:text-red-500 disabled:opacity-30" aria-label="Remove member" data-testid={`guest-member-remove-${i}`}><Trash2 size={15} /></button>
  </div>
);

// Register a whole team without an EvalentX Arena account; accounts are activated later from the emailed link.
export const GuestTeamModal = ({ event, onClose }) => {
  const [quote, setQuote] = useState(null);
  const [teamName, setTeamName] = useState('');
  const [leader, setLeader] = useState(blank);
  const [members, setMembers] = useState([]);
  const [coupon, setCoupon] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const size = members.length + 1;

  const seeded = useRef(false);
  useEffect(() => {
    let live = true;  // drop stale responses when the team size changes quickly
    axios.get(`${API}/api/public/events/${event.id}/team-quote`, { params: { members: size } }).then((r) => {
      if (!live) return;
      setQuote(r.data);
      if (!seeded.current) {
        seeded.current = true;
        if (r.data.min_size > 1) setMembers((prev) => (prev.length ? prev : Array.from({ length: r.data.min_size - 1 }, () => ({ ...blank }))));
      }
    }).catch(() => {});
    return () => { live = false; };
  }, [event.id, size]);
  const priced = quote && quote.members === Math.max(quote.min_size, Math.min(quote.max_size, size));

  const validate = () => {
    if (teamName.trim().length < 2) return 'Enter a team name (2–60 characters)';
    if (leader.name.trim().length < 2 || !EMAIL_RX.test(leader.email) || (leader.phone.replace(/\D/g, '').length < 10)) return 'Enter your full name, a valid email and a 10-digit phone number';
    if (members.some((m) => m.name.trim().length < 2 || !EMAIL_RX.test(m.email))) return 'Enter a name and valid email for every member';
    const emails = [leader.email, ...members.map((m) => m.email)].map((e) => e.trim().toLowerCase());
    if (new Set(emails).size !== emails.length) return 'Each member needs a different email';
    if (quote && (size < quote.min_size || size > quote.max_size)) return `Teams need ${quote.min_size}–${quote.max_size} members`;
    return '';
  };

  const submit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/public/events/${event.id}/guest-team`, {
        team_name: teamName.trim(), leader: { ...leader, email: leader.email.trim() }, coupon_code: coupon.trim(),
        members: members.map((m) => ({ ...m, email: m.email.trim(), phone: '', college: '' })),
      });
      if (data.status === 'payment_required' && data.mode === 'leader' && data.payment_session_id) {
        toast.info('Team saved — opening secure payment…');
        await startCashfree(data.payment_session_id, `${window.location.origin}/events/${event.id}?team_order_id=${data.order_id}&team_id=${data.team_id}&guest=1&status=success`);
        return;
      }
      if (data.status === 'payment_required' && data.mode === 'per_member') {
        const r = await axios.post(`${API}/api/public/events/${event.id}/create-payment?registration_id=${data.registration_id}`);
        if (r.data.payment_session_id) { await startCashfree(r.data.payment_session_id, `${window.location.origin}/events/${event.id}?order_id=${r.data.order_id}&status=success`); return; }
      }
      if (data.status === 'payment_error') toast.error(`${data.detail} — your team is saved; submit again with the same email to retry payment.`);
      setDone(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not register the team');
    } finally { setBusy(false); }
  };

  const b = quote?.price_breakdown;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="guest-team-modal">
      <div className="bg-white w-full sm:max-w-xl max-h-[92vh] overflow-y-auto rounded-t-xl sm:rounded-xl p-5 sm:p-6 text-left">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-[#0A0A0A]">Register your team</h3>
            <p className="text-xs text-[#52525B] mt-1">No account needed. Everyone gets an email to activate their EvalentX Arena account later — tickets and team details link automatically.</p>
          </div>
          <button onClick={onClose} className="p-1 text-[#A1A1AA] hover:text-[#0A0A0A]" aria-label="Close" data-testid="guest-team-close"><X size={20} /></button>
        </div>
        {done ? (
          <div className="mt-5 space-y-3" data-testid="guest-team-success">
            <p className="text-sm font-semibold text-emerald-700">{done.status === 'confirmed' ? 'Team registered! Tickets are on their way to every member.' : `Team ${done.team_name} is saved.`}</p>
            <p className="text-sm text-[#52525B]">Registration ID: <b className="font-mono text-[#0A0A0A]" data-testid="guest-team-reg-id">{done.ticket_code}</b></p>
            <p className="text-xs text-[#52525B]">Check your inbox for the account activation link (valid for 7 days).</p>
            <button onClick={onClose} className="w-full py-2.5 bg-[#0A0A0A] text-white rounded text-sm font-semibold" data-testid="guest-team-done">Done</button>
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            <input className={inp} placeholder="Team name" maxLength={60} value={teamName} onChange={(e) => setTeamName(e.target.value)} data-testid="guest-team-name" />
            <div className="space-y-2">
              <p className="text-xs font-bold uppercase tracking-wider text-[#A1A1AA]">You (team leader)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input className={inp} placeholder="Full name" value={leader.name} onChange={(e) => setLeader({ ...leader, name: e.target.value })} data-testid="guest-leader-name" />
                <input className={inp} type="email" placeholder="Email" value={leader.email} onChange={(e) => setLeader({ ...leader, email: e.target.value })} data-testid="guest-leader-email" />
                <input className={inp} type="tel" placeholder="Phone (10 digits)" value={leader.phone} onChange={(e) => setLeader({ ...leader, phone: e.target.value })} data-testid="guest-leader-phone" />
                <input className={inp} placeholder="College / organisation" value={leader.college} onChange={(e) => setLeader({ ...leader, college: e.target.value })} data-testid="guest-leader-college" />
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-bold uppercase tracking-wider text-[#A1A1AA]">Team members {quote && `(${quote.min_size}–${quote.max_size} incl. you)`}</p>
              {members.map((m, i) => (
                <MemberRow key={i} i={i} m={m} canRemove={!quote || size > quote.min_size}
                  onChange={(v) => setMembers(members.map((x, j) => (j === i ? v : x)))} onRemove={() => setMembers(members.filter((_, j) => j !== i))} />
              ))}
              {(!quote || size < quote.max_size) && (
                <button type="button" onClick={() => setMembers([...members, { ...blank }])} className="inline-flex items-center gap-1 text-xs font-semibold text-[#002FA7]" data-testid="guest-member-add"><Plus size={14} /> Add member</button>
              )}
            </div>
            {quote?.is_paid && (
              <>
                <input className={inp} placeholder="Coupon code (optional)" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} data-testid="guest-team-coupon" />
                <PriceBreakdown b={b} baseLabel={quote.payment_mode === 'leader' ? `Team fee (${quote.members} members)` : 'Your share'} testId="guest-price-breakdown" />
                {coupon && <p className="text-[11px] text-[#A1A1AA]">Coupon discount is applied at payment.</p>}
              </>
            )}
            <button onClick={submit} disabled={busy || !priced} className="w-full py-3 bg-[#002FA7] hover:bg-[#00227A] text-white rounded font-semibold text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="guest-team-submit">
              {busy && <Loader2 size={16} className="animate-spin" />}
              {quote?.is_paid && b?.total > 0 ? `Register & pay INR ${Number(b.total).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : 'Register team'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
